import React from "react";
import { Controller, useFormState, type UseFormReturn } from "react-hook-form";
import { Input, Label, Select, SelectOption } from "@flixbus/honeycomb-react";

import { lineOptions } from "../../../../constants/lines";
import type { CreateCalculationFormData } from "../../CreateCalculationPanel.constants";
import type { SupplierOption } from "../../../../hooks/useCreateCalculationPanel";

const currentYear = new Date().getUTCFullYear();
const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 1 + i);

interface Step1MetadataProps {
  form: UseFormReturn<CreateCalculationFormData>;
  suppliers: SupplierOption[];
}

const Step1Metadata: React.FC<Step1MetadataProps> = ({ form, suppliers }) => {
  const { control } = form;
  // useFormState subscribes this component directly to error changes,
  // so React.memo doesn't prevent re-renders when trigger() populates errors.
  const { errors } = useFormState({ control });

  return (
    <>
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <Input
            id="create-name"
            label={<Label text="Calculation name*" />}
            maxLength={255}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            innerRef={field.ref}
            valid={errors.name ? false : undefined}
            infoError={errors.name?.message}
          />
        )}
      />

      <Controller
        name="kamName"
        control={control}
        render={({ field }) => (
          <Input
            id="create-kam-name"
            label={<Label text="KAM name*" />}
            placeholder="e.g. Jane Doe"
            maxLength={255}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            innerRef={field.ref}
            valid={errors.kamName ? false : undefined}
            infoError={errors.kamName?.message}
          />
        )}
      />

      <Controller
        name="line"
        control={control}
        render={({ field }) => (
          <Select
            id="create-line"
            label={<Label text="Line*" />}
            placeholder="— Select —"
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            innerRef={field.ref}
          >
            <SelectOption value="">{"— Select —"}</SelectOption>
            {lineOptions.map((line) => (
              <SelectOption key={line} value={line}>
                {line}
              </SelectOption>
            ))}
          </Select>
        )}
      />

      <Controller
        name="planningYear"
        control={control}
        render={({ field }) => (
          <Select
            id="create-planning-year"
            label={<Label text="Planning year*" />}
            value={String(field.value)}
            onChange={(e) => field.onChange(Number(e.target.value))}
            onBlur={field.onBlur}
            innerRef={field.ref}
          >
            {yearOptions.map((year) => (
              <SelectOption key={year} value={String(year)}>
                {year}
              </SelectOption>
            ))}
          </Select>
        )}
      />

      <Controller
        name="supplierId"
        control={control}
        render={({ field }) => (
          <Select
            id="create-supplier"
            label={<Label text="Supplier*" />}
            placeholder="— Select —"
            value={field.value ?? ""}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            innerRef={field.ref}
            valid={errors.supplierId ? false : undefined}
            infoError={errors.supplierId?.message}
          >
            <SelectOption value="">{"— Select —"}</SelectOption>
            {suppliers.map((supplier) => (
              <SelectOption key={supplier.id} value={supplier.id}>
                {supplier.name}
              </SelectOption>
            ))}
          </Select>
        )}
      />
    </>
  );
};

export default React.memo(Step1Metadata);
